// src/components/MobileSignaturePage.js
import React, { useState, useRef, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./MobileSignaturePage.css";

const MobileSignaturePage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [signatureData, setSignatureData] = useState({
    id: "",
    position: "",
    name: "",
    vorname: ""
  });
  
  const signaturePadRef = useRef(null);
  
  // Canvas stabilisieren nach dem Rendern
  useEffect(() => {
    const stabilizeCanvas = () => {
      if (signaturePadRef.current && signaturePadRef.current._canvas) {
        const canvas = signaturePadRef.current._canvas;
        canvas.style.touchAction = 'none';
        canvas.style.webkitTouchCallout = 'none';
        canvas.style.webkitUserSelect = 'none';
        canvas.style.userSelect = 'none';
        
        // Event-Listener direkt am Canvas
        const preventDefault = (e) => {
          e.preventDefault();
          e.stopPropagation();
        };
        
        canvas.addEventListener('touchstart', preventDefault, { passive: false });
        canvas.addEventListener('touchmove', preventDefault, { passive: false });
        canvas.addEventListener('touchend', preventDefault, { passive: false });
        
        return () => {
          canvas.removeEventListener('touchstart', preventDefault);
          canvas.removeEventListener('touchmove', preventDefault);
          canvas.removeEventListener('touchend', preventDefault);
        };
      }
    };
    
    const timer = setTimeout(stabilizeCanvas, 200);
    return () => clearTimeout(timer);
  }, []);
  
  // Parameter aus der URL holen
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const id = params.get("id");
      const position = params.get("position");
      const name = params.get("name");
      const vorname = params.get("vorname");
      
      if (!id) {
        setError("Keine gültige Signatur-ID gefunden.");
        setLoading(false);
        return;
      }
      
      setSignatureData({
        id,
        position: position || "Unbekannte Position",
        name: name || "",
        vorname: vorname || ""
      });
      
      // Prüfen, ob bereits eine Unterschrift vorhanden ist
      checkExistingSignature(id);
      
    } catch (e) {
      setError("Fehler beim Laden der Signatur-Daten.");
      setLoading(false);
    }
  }, []);
  
  // Prüfen, ob bereits eine Unterschrift in Firestore existiert
  const checkExistingSignature = async (id) => {
    try {
      const signatureRef = doc(db, "signatures", id);
      const docSnap = await getDoc(signatureRef);
      
      if (docSnap.exists() && docSnap.data().signature) {
        // Es gibt bereits eine Unterschrift
        setSuccess(true);
        setLoading(false);
      } else {
        // Keine Unterschrift vorhanden, normal fortfahren
        setLoading(false);
      }
    } catch (error) {
      console.error("Fehler beim Prüfen vorhandener Unterschriften:", error);
      setLoading(false);
    }
  };

  // Unterschrift löschen
  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };
  
  // Unterschrift speichern und an Firestore senden
  const saveSignature = async () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
      
      try {
        setLoading(true);
        
        // In Firestore speichern
        await setDoc(doc(db, "signatures", signatureData.id), {
          signature: signatureDataURL,
          timestamp: new Date(),
          position: signatureData.position,
          name: signatureData.name,
          vorname: signatureData.vorname
        });
        
        setSuccess(true);
        setLoading(false);
      } catch (e) {
        console.error("Fehler beim Speichern:", e);
        setError("Fehler beim Speichern der Unterschrift. Bitte versuchen Sie es erneut.");
        setLoading(false);
      }
    } else {
      setError("Bitte unterschreiben Sie zuerst, bevor Sie speichern.");
    }
  };
  
  // Wenn der Nutzer zurück zum Formular möchte
  const goBack = () => {
    window.close(); // Schließt das Fenster/Tab, falls es vom QR-Code geöffnet wurde
  };
  
  // Lade-Anzeige
  if (loading) {
    return (
      <div className="mobile-signature-page loading">
        <div className="spinner"></div>
        <p>Wird geladen...</p>
      </div>
    );
  }
  
  // Fehler-Anzeige
  if (error) {
    return (
      <div className="mobile-signature-page error">
        <h2>Fehler</h2>
        <p>{error}</p>
        <button className="primary-button" onClick={goBack}>Zurück</button>
      </div>
    );
  }
  
  // Erfolgs-Anzeige
  if (success) {
    return (
      <div className="mobile-signature-page success">
        <h2>Unterschrift gespeichert!</h2>
        <div className="success-icon">✓</div>
        <p>Ihre Unterschrift wurde erfolgreich gespeichert.</p>
        <p>Sie können dieses Fenster jetzt schließen.</p>
        <button className="primary-button" onClick={goBack}>Schließen</button>
      </div>
    );
  }
  
  // Normale Ansicht zum Unterschreiben
  return (
    <div className="mobile-signature-page">
      <h2>Unterschrift</h2>
      
      <div className="signature-info">
        <p><strong>Position:</strong> {signatureData.position}</p>
        {signatureData.name && (
          <p><strong>Name:</strong> {signatureData.name} {signatureData.vorname}</p>
        )}
      </div>
      
      <div className="signature-pad-container">
        <p>Bitte unterschreiben Sie hier:</p>
        <div 
          className="signature-canvas-wrapper"
          style={{
            border: '1px solid #ccc',
            borderRadius: '4px',
            width: '100%',
            height: '200px',
            backgroundColor: '#fff',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <SignatureCanvas
            ref={signaturePadRef}
            penColor="black"
            backgroundColor="white"
            minWidth={2}
            maxWidth={3}
            throttle={16}
            velocityFilterWeight={0.7}
            canvasProps={{
              className: "signature-canvas",
              style: {
                touchAction: 'none',
                width: '100%',
                height: '100%',
                display: 'block'
              }
            }}
          />
        </div>
      </div>
      
      <div className="button-container">
        <button className="secondary-button" onClick={clearSignature}>Löschen</button>
        <button className="primary-button" onClick={saveSignature}>Unterschrift speichern</button>
      </div>
    </div>
  );
};

export default MobileSignaturePage;